import React, { useContext, useEffect, useState } from 'react';
import {
  ActionTypeEnum,
  CompanyLocationRampWithOpeningHoursOutDTO,
  ExpeditionRouteV4Response,
  LocationPreviewV4OutDTO,
  TimeSlotResponse,
} from '@api/ringil-api.v2';
import RingilDataTable from '@components/ringil3/RingilDataTable/RingilDataTable';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import styled, { css } from 'styled-components';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import { RowWithGap } from '@components/styles';
import { Clickable } from '@components/ringil3/styles';
import LinkedResBadge from '@components/ringil3/Badges/LinkedResBadge';
import {
  useCreateCreateReservationDialog,
} from '@components/ringil3/Features/Expedition/Dialogs/CreateReservationDialog';
import {
  useCreateAskForCarrierReservation,
} from '@components/ringil3/Features/Expedition/Sidebars/AskForCarrierReservation';
import { useCreateTimeslotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/CreateTimeslotSidebar';
import CanMakeReservation from '@components/ringil3/Features/Reservation/Helper/CanMakeReservation';
import { ApiContext } from '@api/api';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import { useEditActionSlotSidebar } from '@components/ringil3/Features/Expedition/Sidebars/EditActionSlotSidebar.tsx';
import {
  dayjsAtStartOfDayInTz,
  dayjsFromApiUtcInTz,
  dayjsFromLocalDateString,
  dayjsNowInTz,
  formatDateFromDayjsWithLocale, formatTimeFromDayjsInTz, formatTimeFromDayjsWithLocale,
  formatTimeIntervalFromDayjsIntervalWithLocale,
  globalDayWithIntervalComparator,
  stringYMDFromDayjs,
} from '@utils/dateWithTz.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { getLocationPositionKey } from '@utils/testUtils';

interface ComponentProps {
  onChanged: () => void;
  routes: ExpeditionRouteV4Response[];
  applicationId: string;
  companyId: string;
  canEdit: boolean;
  locations: LocationPreviewV4OutDTO[];
}

const RouteTable: React.FC<ComponentProps> = ({ onChanged, routes, applicationId, companyId, canEdit, locations }) => {
  const { tr } = useTranslationLgs();
  const { sendExpeditionTimeslotReminder, getSharedRampsForCompanyLocation } = useContext(ApiContext);
  const [rampsByLocationId, setRampsByLocationId] = useState<Record<string, CompanyLocationRampWithOpeningHoursOutDTO[]>>({});
  const [rowData, setRowData] = useState<ExpeditionRouteV4Response>(null);

  const {
    CreateReservationDialog,
    setCreateTimeslotRequest: setCreateTimeslotFirstRequest,
  } = useCreateCreateReservationDialog();
  const { CreateTimeslotSidebar, setCreateTimeslotRequest, createTimeslotRequest } = useCreateTimeslotSidebar();
  const { setVisible: setVisibleAskForCarrierReservation } = useCreateAskForCarrierReservation();
  const { EditActionSlotSidebar, setVisible: setVisibleActionSlotSidebar } = useEditActionSlotSidebar();

  useEffect(() => {
    if (routes) {
      routes.forEach(route => {
        const companyLocationIdForRampFetching = route.companyLocationId;
        const customerIdAtLocation = route.companyLocationCustomerId;

        if (companyLocationIdForRampFetching && customerIdAtLocation && rampsByLocationId[companyLocationIdForRampFetching] === undefined) {
          setRampsByLocationId(prevRamps => ({ ...prevRamps, [companyLocationIdForRampFetching]: null }));
          getSharedRampsForCompanyLocation(companyLocationIdForRampFetching, customerIdAtLocation, true, (ramps) => {
            setRampsByLocationId(prevRamps => ({ ...prevRamps, [companyLocationIdForRampFetching]: ramps || [] }));
          });
        }
      });
    }
  }, [routes, getSharedRampsForCompanyLocation]);

  const getRouteTestIdPrefix = (row: ExpeditionRouteV4Response, index: number) => {
    const locationKey = getLocationPositionKey(row.expeditionLocationId, index, routes.length);
    return `carrierSelection-route-${locationKey}`;
  };

  const renderPosition = (row: ExpeditionRouteV4Response, index: number): JSX.Element => {
    return <Position>{index + 1}.</Position>;
  };

  const renderPlace = (row: ExpeditionRouteV4Response, index: number): JSX.Element => {
    const testIdPrefix = getRouteTestIdPrefix(row, index);
    return (
      <div data-testid={`${testIdPrefix}-locationName`}>
        <div>
          {row.routeName} {!row.isPartnerLocation && row.companyName && <>({row.companyName})</>}
        </div>
        <div>{row.addressLine}</div>
      </div>
    );
  };

  const renderAction = (row: ExpeditionRouteV4Response, index: number): JSX.Element => {
    const testIdPrefix = getRouteTestIdPrefix(row, index);
    return (
      <div data-testid={`${testIdPrefix}-loadingUnloading`}>
          {row.actionType.map((action, actionIndex) => (
            <COBadge key={actionIndex} type={action == ActionTypeEnum.LOADING ? 'loading' : 'unloading'}
                     variant={'filled'}>
              {tr(`ExpeditionDetail.action.${action}`, action)}
            </COBadge>
          ))}
      </div>
    );
  };

  const sendReminder = (locationId: string) => {
    sendExpeditionTimeslotReminder(companyId, applicationId, locationId, () => {
    });
  };

  const computePreferredDate = (
    selectedSlot: TimeSlotResponse | null,
    locationId: string,
  ): string => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (isNullOrUndefined(selectedSlot?.timezoneId) && selectedSlot?.localDate) {
      return new Date(selectedSlot?.localDate) < now ? new Date().toISOString().split('T')[0] : selectedSlot?.localDate;
    }

    const location = locations.find(loc => loc.id === locationId)
    
    if (location?.deliverySlot?.dayWithInterval?.length) {
      const dayjsAtStartOfDay = location.timezoneId != null
        ? dayjsAtStartOfDayInTz(dayjsNowInTz(location.timezoneId), location.timezoneId)
        : dayjsAtStartOfDayInTz(dayjsNowInTz('Europe/Prague'), 'Europe/Prague');

      const firsstFutureSlot = location.deliverySlot.dayWithInterval
        .filter(slot => {
          const slotDayjs = location.timezoneId != null
            ? dayjsFromApiUtcInTz(slot.utcDay, location.timezoneId)
            : dayjsFromApiUtcInTz(slot.day, 'Europe/Prague');
          return slotDayjs.isAfter(dayjsAtStartOfDay);
        })
        .filter(slot => slot.openingMode !== 'CLOSED')
        .sort((a, b) => globalDayWithIntervalComparator(a, b, location.timezoneId))[0];

      if (firsstFutureSlot) {
        const date = dayjsFromLocalDateString(firsstFutureSlot.utcDay);
        return stringYMDFromDayjs(date);
      }
    }

    return new Date().toISOString().split('T')[0];
  };

  const renderCarrierSlot = (row: ExpeditionRouteV4Response, index: number): JSX.Element => {
    const testIdPrefix = getRouteTestIdPrefix(row, index);
    const since = row.selectedSlot?.timezoneId != null
      ? dayjsFromApiUtcInTz(row.selectedSlot?.utcStart, row.selectedSlot?.timezoneId)
      : dayjsFromLocalDateString(row.selectedSlot?.localDate);
    const till = row.selectedSlot?.timezoneId != null
      ? dayjsFromApiUtcInTz(row.selectedSlot?.utcEnd, row.selectedSlot?.timezoneId) : null;
    const plannedDate = formatDateFromDayjsWithLocale(since);
    const timeInterval = row.selectedSlot?.timezoneId != null
      ? formatTimeIntervalFromDayjsIntervalWithLocale(since, till, row.selectedSlot?.timezoneId)
      : `${row.selectedSlot?.sinceHourString} - ${row.selectedSlot?.tillHourString}`;
    
    return (
      <>
        <div data-testid={`${testIdPrefix}-selectedTimeslot`}>
            {row.selectedSlot && (
              <>
                <RowWithGap data-testid={`${testIdPrefix}-selectedTimeslot-date`}>
                  <CalendarIcon type={'planned'} />
                  <PlannedTime color={computeColor('planned')}>{plannedDate}</PlannedTime>
                </RowWithGap>
                <RowWithGap data-testid={`${testIdPrefix}-selectedTimeslot-time`}>
                  <ClockIcon type={'planned'} />
                  <div>{timeInterval}</div>
                </RowWithGap>
                <COTimezone dataTestId={`${testIdPrefix}-selectedTimeslot-timezone`} timezoneId={row.selectedSlot.timezoneId} />
              </>
            )}

            <CanMakeReservation companyLocationId={row.companyLocationId}>
              {isNullOrUndefined(row.linkedReservation) &&
                canEdit === true &&
                !row.isPartnerLocation &&
                Array.isArray(rampsByLocationId[row.companyLocationId]) &&
                rampsByLocationId[row.companyLocationId].length > 0 && (
                  <Clickable
                    onClick={() =>
                      setCreateTimeslotFirstRequest({
                        applicationId: applicationId,
                        companyLocationId: row.companyLocationId,
                        preferredDate: computePreferredDate(row.selectedSlot, row.expeditionLocationId),
                        companyLocationCustomerId: row.companyLocationCustomerId,
                        expeditionLocationId: row.expeditionLocationId,
                        expeditionId: row.expeditionId,
                        timezoneId: row.selectedSlot?.timezoneId,
                        testIdPrefix: `${testIdPrefix}-selectedTimeslot`,
                      })
                    }
                  >
                    {tr(`RouteTable.createReservation`, `Create reservation`)}
                  </Clickable>
                )}
            </CanMakeReservation>

            {row.linkedReservation && (
              <div data-testid={`${testIdPrefix}-reservationId`}>
                <LinkedResBadge linkedReservation={row.linkedReservation} />
              </div>
            )}

        </div>
        <CreateReservationDialog
          onCreateReservationClick={v => {
            setCreateTimeslotFirstRequest(undefined);
            setCreateTimeslotRequest(v);
          }}
          onAskCarrierClick={() => {
            sendReminder(row.companyLocationId);
            setVisibleAskForCarrierReservation(true);
          }}
        />
      </>
    );
  };

  const renderActionTime = (row: ExpeditionRouteV4Response, index: number): JSX.Element => {
    const testIdPrefix = getRouteTestIdPrefix(row, index);

    const plannedDate = (isNotNullOrUndefined(row.actionSlot?.timezoneId) && isNotNullOrUndefined(row.actionSlot?.actionAtUtc))
      ? formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(row.actionSlot.actionAtUtc, row.actionSlot.timezoneId))
      : row.actionSlot?.localDate;
    const plannedTime = (isNotNullOrUndefined(row.actionSlot?.timezoneId) && isNotNullOrUndefined(row.actionSlot?.actionAtUtc))
      ? formatTimeFromDayjsWithLocale(dayjsFromApiUtcInTz(row.actionSlot.actionAtUtc, row.actionSlot.timezoneId))
      : row.actionSlot?.hourString;

    return (
      <div data-testid={`${testIdPrefix}-actualHandling`}>
        {row.actionSlot && (
          <>
            <RowWithGap>
              <CalendarIcon type={row.isLate ? 'late' : 'on-time'} />
              <PlannedTime
                data-testid={`${testIdPrefix}-actualHandling-date`} color={computeColor(row.isLate ? 'late' : 'on-time')}>{plannedDate}</PlannedTime>
              <SvgPencil className="text-content-brand" onClick={() => {
                setRowData(row);
                setVisibleActionSlotSidebar(true);
              }}
              />
            </RowWithGap>
            <RowWithGap>
              <ClockIcon type={row.isLate ? 'late' : 'on-time'} />
              <PlannedTime
                data-testid={`${testIdPrefix}-actualHandling-time`} color={computeColor(row.isLate ? 'late' : 'on-time')}>{plannedTime}</PlannedTime>
            </RowWithGap>
            <COTimezone timezoneId={row.actionSlot.timezoneId} dataTestId={`${testIdPrefix}-actualHandling-timezone`} />
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <RingilDataTable
        data={routes}
        columns={[
          { name: '' },
          { name: tr('ExpeditionDetail.place', 'Place') },
          { name: tr('ExpeditionDetail.type', 'Type') },
          { name: tr('ExpeditionDetail.timeSlot', 'Time slot') },
          { name: tr('ExpeditionDetail.status', 'Status') },
        ]}
        renderers={[renderPosition, renderPlace, renderAction, renderCarrierSlot, renderActionTime]}
      />

      {createTimeslotRequest && (
        <>
          <CreateTimeslotSidebar
            onContinue={() => {
              onChanged();
            }}
          />
        </>
      )}

      {rowData && <EditActionSlotSidebar
        applicationId={applicationId}
        expeditionId={rowData.expeditionId}
        locationId={rowData.expeditionLocationId}
        locationName={locations.find(item => item.id === rowData.expeditionLocationId)?.name || ''}
        actionSlot={rowData.actionSlot}
        onContinue={() => {
          onChanged();
        }}
      />
      }
    </>
  );
};

const computeColor = (type: string) => {
  return type === 'planned' ? 'var(--ringil3-content-tertiary-color)' : type === 'late' ? 'var(--ringil3-complementary-error-color)' : 'var(--ringil3-gr4)';
};

interface Props {
  type: 'planned' | 'late' | 'on-time';
}

const ClockIcon: React.FC<Props> = ({ type }) => {
  const fill = computeColor(type);
  return (
    <>
      <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8 16.5C12.4183 16.5 16 12.9183 16 8.5C16 4.08172 12.4183 0.5 8 0.5C3.58172 0.5 0 4.08172 0 8.5C0 12.9183 3.58172 16.5 8 16.5ZM7 3.5V8.91421L10.2929 12.2071L11.7071 10.7929L9 8.08579V3.5H7Z"
          fill={fill}
        />
      </svg>
    </>
  );
};

const CalendarIcon: React.FC<Props> = ({ type }) => {
  const fill = computeColor(type);
  return (
    <>
      <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 0.5H3V2.5H1V5.5H15V2.5H13V0.5H10V2.5H6V0.5Z" fill={fill} />
        <path d="M15 7.5H1V15.5H15V7.5Z" fill={fill} />
      </svg>
    </>
  );
};

const PlannedTime = styled.div<{ color: string }>`
  ${props =>
      props &&
      props.color &&
      css`
        color: ${props.color};
      `}
`;

const Position = styled.div`
  display: flex;
  color: var(--ringil3-brand-color);
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 16px;
  line-height: 21px;
`;

export default RouteTable;
